import React from "react";
import { Link } from "react-router-dom";
import loanProductBanner from "../../assets/loan-product-banner.webp";
import {
  ShieldCheck,
  Percent,
  Headphones,
  ArrowDown,
  Sparkles,
} from "lucide-react";

function LoanHero() {
  return (
    <section className="relative overflow-hidden bg-[#f7faf8] pt-5 pb-12 sm:pt-8 sm:pb-16 lg:pt-10 lg:pb-20">
      {/* Soft Ambient Radial Background Glows */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_12%_35%,rgba(46,229,162,0.12),transparent_40%),radial-gradient(circle_at_88%_45%,rgba(0,135,81,0.08),transparent_40%)]"
      />

      <div className="section-container relative z-10">
        
        {/* ================= 1. BREADCRUMB ================= */}
        <nav className="flex items-center gap-2 font-jakarta text-[12.5px] sm:text-[13px] text-[#6b7280] mb-5 sm:mb-8">
          <Link
            to="/"
            className="hover:text-[var(--hero-green)] transition-colors"
          >
            Home
          </Link>
          <span className="text-[#9ca3af]">›</span>
          <span className="font-semibold text-[#111827]">
            Loan Products
          </span>
        </nav>

        {/* ================= 2. MAIN HERO 2-COLUMN FULLY RESPONSIVE GRID ================= */}
        <div className="grid grid-cols-1 items-center gap-8 sm:gap-10 lg:grid-cols-12 lg:gap-8 xl:gap-12">
          
          {/* LEFT COLUMN: Narrative, Headline, Subtitle, Key Features & Single Action Button */}
          <div className="lg:col-span-6 xl:col-span-6">
            
            {/* Tag Badge */}
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--hero-green-light)] px-3 py-1 font-jakarta text-[11.5px] sm:text-[12px] font-bold text-[var(--hero-green)] border border-[var(--hero-green-border)] shadow-xs mb-3.5 sm:mb-4">
              <Sparkles size={13} className="text-[var(--hero-green)]" />
              <span>FUND YOUR DREAMS</span>
            </span>

            {/* Main Headline */}
            <h1 className="font-syne text-[28px] sm:text-[38px] md:text-[46px] lg:text-[54px] xl:text-[60px] font-bold leading-[1.15] tracking-[-0.5px] sm:tracking-[-1.5px] text-[#111827] text-balance">
              Smart Loans <span className="text-[var(--hero-green)]">For Every Goal.</span>
            </h1>

            {/* Sub-headline */}
            <p className="mt-4 sm:mt-5 max-w-[560px] font-jakarta text-[15px] sm:text-[16.5px] lg:text-[17px] leading-[1.65] text-[#556369]">
              Flexible loan solutions designed to support your lifestyle, business, and future aspirations with quick digital approvals and transparent interest rates.
            </p>

            {/* 3 Key Feature Badges (Matching Reference Mockup) */}
            <div className="mt-6 sm:mt-7 flex flex-wrap items-center gap-4 sm:gap-6 pt-1">
              
              {/* Feature 1 */}
              <div className="flex items-center gap-2.5">
                <div className="flex h-9 w-9 shrink-0 aspect-square items-center justify-center rounded-xl bg-blue-50 border border-blue-200 text-blue-600 shadow-2xs">
                  <ShieldCheck size={18} strokeWidth={2.2} />
                </div>
                <div>
                  <p className="font-bricolage text-[13.5px] sm:text-[14px] font-bold text-[#111827] leading-tight">
                    Quick Approval
                  </p>
                  <p className="font-jakarta text-[11.5px] text-[#6b7280]">
                    Hassle-free process
                  </p>
                </div>
              </div>

              {/* Feature 2 */}
              <div className="flex items-center gap-2.5">
                <div className="flex h-9 w-9 shrink-0 aspect-square items-center justify-center rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-600 shadow-2xs">
                  <Percent size={17} strokeWidth={2.2} />
                </div>
                <div>
                  <p className="font-bricolage text-[13.5px] sm:text-[14px] font-bold text-[#111827] leading-tight">
                    Competitive Rates
                  </p>
                  <p className="font-jakarta text-[11.5px] text-[#6b7280]">
                    Best rates in market
                  </p>
                </div>
              </div>

              {/* Feature 3 */}
              <div className="flex items-center gap-2.5">
                <div className="flex h-9 w-9 shrink-0 aspect-square items-center justify-center rounded-xl bg-amber-50 border border-amber-200 text-amber-600 shadow-2xs">
                  <Headphones size={17} strokeWidth={2.2} />
                </div>
                <div>
                  <p className="font-bricolage text-[13.5px] sm:text-[14px] font-bold text-[#111827] leading-tight">
                    Dedicated Support
                  </p>
                  <p className="font-jakarta text-[11.5px] text-[#6b7280]">
                    We&apos;re here to help
                  </p>
                </div>
              </div>

            </div>

            {/* Single Action Button: Explore All Loans with Green Background */}
            <div className="mt-7 sm:mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
              <a
                href="#popular-loans"
                className="inline-flex h-[50px] items-center justify-center gap-2.5 rounded-full bg-[var(--hero-green)] px-8 font-jakarta text-[15px] font-bold text-white shadow-md transition-all duration-200 hover:-translate-y-0.5 hover:bg-[var(--hero-green-hover)] hover:shadow-lg outline-none focus:outline-none cursor-pointer"
              >
                <span>Explore All Loans</span>
                <ArrowDown size={16} strokeWidth={2.4} />
              </a>
            </div>

          </div>

          {/* RIGHT COLUMN: Static 3D Loan Products Banner (No Animation / No Scale) */}
          <div className="lg:col-span-6 xl:col-span-6 flex items-center justify-center pt-2 lg:pt-0">
            <div className="relative flex items-center justify-center w-full max-w-[480px] sm:max-w-[540px] lg:max-w-[580px] xl:max-w-[620px]">
              
              {/* Soft Ambient Mint Halo */}
              <div
                aria-hidden="true"
                className="pointer-events-none absolute h-[260px] w-[260px] sm:h-[340px] sm:w-[340px] lg:h-[400px] lg:w-[400px] rounded-full bg-[radial-gradient(circle,rgba(46,229,162,0.35),transparent_70%)] blur-3xl"
              />

              {/* Static Cutout 3D Banner Image */}
              <img
                src={loanProductBanner}
                alt="Finverse Smart Loans For Every Goal"
                className="relative z-10 w-full h-auto max-h-[340px] sm:max-h-[420px] lg:max-h-[480px] object-contain drop-shadow-[0_20px_45px_rgba(0,0,0,0.12)]"
                loading="eager"
              />
            </div>
          </div>

        </div>

      </div>
    </section>
  );
}

export default LoanHero;
